import React, { useState } from 'react';

import {
  Button,
  CopyTextInput,
  Field,
  Grid,
  IconButton,
  Spinner,
  Text,
} from '@zapier/design-system';

import { PartnerUserInteractionEventTracker } from 'app/developer-v3/teams/team-partner-sharing/createInteractionTracker';
import {
  useOAuthApp,
  useRegenerateOAuthAppClientSecret,
} from 'app/developer-v3/hooks';

const LOADING_TEXT = 'Loading...';

type Props = {
  slug: string;
  trackInteraction: PartnerUserInteractionEventTracker;
};

function ClientIDAndSecret({ trackInteraction, slug }: Props) {
  const [clientSecret, setClientSecret] = useState<string>(undefined);
  const [isClientSecretVisible, setIsClientSecretVisible] = useState(false);

  const oauthAppResult = useOAuthApp({ slug });
  const clientID = (() => {
    switch (oauthAppResult.type) {
      case 'ok':
        return oauthAppResult.data.clientID;
      case 'loading':
        return LOADING_TEXT;
      default:
        return oauthAppResult.error.message;
    }
  })();

  const isOauthAppReady = oauthAppResult.type === 'ok';

  const {
    isMutating,
    mutate: regenerateClientSecret,
  } = useRegenerateOAuthAppClientSecret();

  return (
    <Grid gap="15px">
      <Field
        label="Client ID"
        renderInput={inputProps => (
          <CopyTextInput
            {...inputProps}
            aria-label="Client ID"
            name="clientID"
            onClick={() => {
              trackInteraction({
                eventAction: 'copy',
                interactionName: 'copy_client_id',
                interactionGoal: 'Copy client ID for Partner API access',
              });
            }}
            value={clientID}
          />
        )}
      />
      <Field
        label="Client secret"
        renderHelpText={() =>
          clientSecret && (
            <Text>
              Make sure you save it after generation - you won’t be able to
              access it again if you navigate away.
            </Text>
          )
        }
        renderInput={inputProps => (
          <Grid alignItems="center" columns="1fr min-content" gap="15px">
            <CopyTextInput
              {...inputProps}
              appendContent={
                isMutating ? (
                  <Spinner />
                ) : (
                  clientSecret &&
                  isOauthAppReady && (
                    <IconButton
                      icon={isClientSecretVisible ? 'miscEyeClosed' : 'miscEye'}
                      onClick={() =>
                        setIsClientSecretVisible(!isClientSecretVisible)
                      }
                    />
                  )
                )
              }
              aria-label="Client secret"
              canClickIconAfter={!!clientSecret}
              name="clientSecret"
              onClick={() => {
                trackInteraction({
                  eventAction: 'copy',
                  interactionName: 'copy_client_secret',
                  interactionGoal: 'Copy client secret for Partner API access',
                });
              }}
              readOnlyText={
                clientSecret ? undefined : 'Revealed only upon creation'
              }
              type={clientSecret && isClientSecretVisible ? 'text' : 'password'}
              value={
                clientSecret && isClientSecretVisible
                  ? clientSecret
                  : '********************'
              }
            />
            <Button
              disabled={!isOauthAppReady}
              onClick={async () => {
                // This is a redundant check. But TS needs it to infer that
                // oauthAppResult.data is valid.
                if (oauthAppResult.type === 'ok') {
                  setIsClientSecretVisible(true);
                  const {
                    clientSecret: clientSecretNext,
                  } = await regenerateClientSecret({
                    id: oauthAppResult.data.id,
                  });
                  setClientSecret(clientSecretNext);
                }
              }}
            >
              Regenerate
            </Button>
          </Grid>
        )}
      />
    </Grid>
  );
}

export default ClientIDAndSecret;
